import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Avatar,
  AvatarGroup,
  Divider,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ImageBaseURL } from '../../utilities/api/axios';

const statusColors = {
  1: { background: '#FFE5E5', color: '#C90505' }, // Archived
  2: { background: '#FEFFD2', color: '#D0A300' }, // In Progress
  3: { background: '#E5FFF1', color: '#057D35' }, // Completed
};

const ProjectCard = ({ project, onView, getUserDetails, allUser }) => {
  const status = project.progress_status;
  const statusColor = statusColors[status];

  const getUserById = userId => allUser.find(user => user.id === userId);

  const creator = getUserById(project.student);
  const teammates = project.teammates
    .map(teammateId => getUserById(teammateId))
    .filter(Boolean);

  const getProfilePicUrl = profilePicPath => {
    return profilePicPath ? (profilePicPath.startsWith('http') ? profilePicPath : `${ImageBaseURL}${profilePicPath}`) : '';
  };

  return (
    <Card
      sx={{
        width: 'auto',
        margin: 1,
        minHeight: '204px',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: '1px solid #D3D3D3',
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 1,
              backgroundColor: statusColor.background,
              borderRadius: '50px',
              width: 'fit-content',
              padding: '0 8px',
            }}
          >
            <Box
              sx={{
                height: 5,
                width: 5,
                borderRadius: '50%',
                backgroundColor: statusColor.color,
                marginRight: 0.5,
                marginLeft: 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: statusColor.color, marginLeft: 1 }}
            >
              {status === '1'
                ? 'Archived'
                : status === '2'
                  ? 'In Progress'
                  : 'Completed'}
            </Typography>
          </Box>

          <Button onClick={() => onView(project)} sx={{ mr: -2, p: 0 }}>
            <VisibilityIcon sx={{ color: 'grey' }} />
          </Button>
        </Box>

        <Box sx={{ textAlign: 'left', padding: '0 8px', borderRadius: '8px' }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: '20px', color: '#007BFF' }}
          >
            {project.title}
          </Typography>

          <Typography sx={{ fontWeight: 500, fontSize: '16px', marginTop: 2 }}>
            Description
          </Typography>

          <Tooltip
            title={project.description || ''}
            arrow
            placement="bottom-start"
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: '14px',
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                textOverflow: 'ellipsis',
                my: 1,
                cursor: 'pointer',
              }}
            >
              {project.description}
            </Typography>
          </Tooltip>

          <Typography variant="body2" sx={{ fontSize: '16px' }}>
            {'Type:  '}
            <Typography
              variant="body2"
              component="span"
              color="textSecondary"
              sx={{ fontSize: '16px' }}
            >
              {project.type === '1'
                ? 'Academic'
                : project.type === '2'
                  ? 'Research'
                  : project.type === '3'
                    ? 'Personal'
                    : 'Other'}
            </Typography>
          </Typography>

          <Typography variant="body2" sx={{ fontSize: '16px', mt: 1 }}>
            {'Team Lead: '}
            <Typography
              variant="body2"
              component="span"
              color="textSecondary"
              sx={{ fontSize: '16px' }}
            >
              {creator ? creator.name : '-'}
            </Typography>
          </Typography>
        </Box>
      </CardContent>

      <Divider />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 16px',
        }}
      >
        <Typography variant="body2" sx={{ color: '#5C5C5C' }}>
          {project.date}
        </Typography>

        <AvatarGroup
          max={15}
          sx={{
            '& .MuiAvatar-root': {
              width: 24,
              height: 24,
              fontSize: 12,
            },
          }}
        >
          {teammates.map((member, index) => (
            <Avatar
              key={index}
              src={getProfilePicUrl(member.profile_pic) || ''}
              alt={member.name}
              title={member.name}
            />
          ))}

          {creator && (
            <Avatar
              src={getProfilePicUrl(creator.profile_pic) || ''}
              alt={creator.name}
              title={creator.name}
              sx={{
                border: '2px solid #007BFF',
              }}
            />
          )}
        </AvatarGroup>
      </Box>
    </Card>
  );
};

export default ProjectCard;
